import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from 'framer-motion';

const INTENT_DISTANCE = 12;
const FLICK_DISTANCE = 32;
const FLICK_SPEED = .55; // px/ms, measured over the end of the drag
const interactive = 'button,a,input,textarea,select,label,summary,svg,canvas,img,iframe,video,audio,[role="img"],[role="button"],[role="link"],[role="textbox"],[role="combobox"],[role="slider"],[role="spinbutton"],[role="checkbox"],[role="radio"],[role="switch"],[role="listbox"],[role="option"],[role="menuitem"],[contenteditable]:not([contenteditable="false"]),[data-swipe-ignore]';
const protectedControl = 'input,textarea,select,label,summary,canvas,iframe,video,audio,[role="textbox"],[role="combobox"],[role="slider"],[role="spinbutton"],[role="checkbox"],[role="radio"],[role="switch"],[role="listbox"],[role="option"],[role="menuitem"],[contenteditable]:not([contenteditable="false"]),[data-swipe-ignore],[data-swipe-media],button:disabled,[aria-disabled="true"]';
const chart = '[role="img"]:not(img),.recharts-wrapper,.recharts-surface,[data-chart]';
const editable = 'input:not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="checkbox"]):not([type="radio"]):not([type="range"]),textarea,select,[contenteditable]:not([contenteditable="false"])';

export function swipeIntent(dx, dy, axis = 'x') {
  const along = axis === 'x' ? dx : dy, across = axis === 'x' ? dy : dx;
  return Math.abs(along) > INTENT_DISTANCE && Math.abs(along) > Math.abs(across) * 1.5;
}

export function swipeDragOffset(amount, { available = true, width = 400, baseOffset = 0 } = {}) {
  const direction = Math.sign(amount), travel = Math.max(0, Math.abs(amount) - INTENT_DISTANCE);
  if (!available) return baseOffset + direction * 12 * (1 - Math.exp(-travel / 120));
  // Follow the finger directly for a normal drag. Resistance only arrives near
  // the viewport edge, where dragging more should not expose an empty page.
  const limit = Math.max(120, Math.min(260, width * .65));
  const remaining = Math.max(12, limit - direction * baseOffset), free = remaining * .65;
  const movement = travel <= free ? travel : free + (remaining - free) * (1 - Math.exp(-(travel - free) / (remaining - free)));
  return baseOffset + direction * movement;
}

function scrollingAncestor(target, surface, axis) {
  for (let node = target; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    const scrolls = axis === 'x'
      ? node.scrollWidth > node.clientWidth + 3 && /^(auto|scroll)$/.test(style.overflowX)
      : node.scrollHeight > node.clientHeight + 3 && /^(auto|scroll)$/.test(style.overflowY);
    if (scrolls) return true;
    if (node === surface) break;
  }
  return false;
}

function canStart(target, surface, startAnywhere) {
  // Opted-in tap targets share their horizontal drag with navigation. Editors and
  // media always keep their own gestures; guards above a nested surface do not apply.
  const marked = target.closest?.('[data-swipe-start]');
  const optedIn = marked && surface.contains(marked);
  const thumbnail = target.closest?.('[data-swipe-thumbnail]');
  for (let node = target; node; node = node.parentElement) {
    if (node.matches?.(protectedControl) || node.matches?.(chart)) return false;
    if (node.matches?.('img') && (!startAnywhere || !thumbnail || !surface.contains(thumbnail))) return false;
    if (startAnywhere && node.matches?.('svg') && node.getAttribute('aria-hidden') !== 'true' && !node.closest('button,a,[role="button"],[role="link"]')) return false;
    if (!optedIn && !startAnywhere && node.matches?.(interactive)) return false;
    if (node === surface) break;
  }
  return true;
}

function releaseCapture(current) {
  try { current?.target.releasePointerCapture?.(current.id); } catch (_) { /* The browser may have already released a cancelled pointer. */ }
}

export function useSwipeMotion({ onPrevious, onNext, axis = 'x', enabled = true, threshold = 64, allowDialog = false, resetOnCommit = true, startAnywhere = false, transitionKey }) {
  const offset = useMotionValue(0), contentOpacity = useMotionValue(1), progress = useMotionValue(0), feedbackY = useMotionValue(12), reduced = useReducedMotion();
  const gesture = useRef(null), animations = useRef([]), animationId = useRef(0), handoff = useRef(null), previousKey = useRef(transitionKey), touchPointers = useRef(new Set()), pendingClick = useRef(null), visualRef = useRef(null), visualState = useRef(null);
  const options = useRef(null);
  options.current = { onPrevious, onNext, axis, enabled, threshold, allowDialog, reduced, resetOnCommit, startAnywhere, transitionKey };

  const setPhase = useCallback(phase => {
    const node = visualRef.current;
    if (phase === 'rest') {
      const previous = visualState.current;
      if (previous) { previous.node.removeAttribute('data-swipe-motion'); previous.node.style.willChange = previous.willChange; }
      visualState.current = null;
    } else if (node) {
      if (!visualState.current || visualState.current.node !== node) visualState.current = { node, willChange: node.style.willChange };
      node.dataset.swipeMotion = phase;
      node.style.willChange = 'transform, opacity';
    }
  }, []);
  const clearHandoff = useCallback(() => {
    if (handoff.current?.timer) clearTimeout(handoff.current.timer);
    if (pendingClick.current?.expires === Infinity) pendingClick.current.expires = Date.now() + 500;
    handoff.current = null;
  }, []);

  const stopAnimation = useCallback(() => {
    animationId.current += 1;
    animations.current.forEach(animation => animation.stop());
    animations.current = [];
  }, []);
  const reset = useCallback(() => {
    clearHandoff();
    stopAnimation();
    if (options.current.reduced) { offset.set(0); contentOpacity.set(1); progress.set(0); setPhase('rest'); return; }
    setPhase('return');
    const currentAnimation = animationId.current;
    const spring = { type: 'spring', stiffness: 340, damping: 34, mass: .85 };
    animations.current = [
      animate(offset, 0, { ...spring, onComplete: () => { if (animationId.current === currentAnimation) setPhase('rest'); } }),
      animate(contentOpacity, 1, spring), animate(progress, 0, spring),
    ];
  }, [offset, contentOpacity, progress, stopAnimation, clearHandoff, setPhase]);
  const suppressClick = useCallback(current => {
    if (current?.locked) pendingClick.current = { target: current.tapTarget, surface: current.target, expires: Date.now() + 500 };
  }, []);
  const cancel = useCallback(() => {
    const current = gesture.current;
    gesture.current = null;
    suppressClick(current);
    releaseCapture(current);
    if (current || handoff.current || offset.get() !== 0 || contentOpacity.get() !== 1 || progress.get() !== 0) reset();
  }, [offset, contentOpacity, progress, reset, suppressClick]);

  useLayoutEffect(() => {
    const changed = !Object.is(previousKey.current, transitionKey);
    previousKey.current = transitionKey;
    if (!changed) return;
    const pending = handoff.current;
    if (!pending) { cancel(); return; }
    if (pending.phase !== 'awaiting') { reset(); return; }
    clearHandoff(); stopAnimation();
    // A slow frame must not let the compatibility click choose a date in the
    // newly rendered month. Start its short expiry after the handoff settles.
    const clickGuard = pending.clickGuard;
    if (pendingClick.current === clickGuard && clickGuard) clickGuard.expires = Infinity;
    // The new content starts on the side it came from and continues in the
    // swipe direction. This happens before paint, so the swap cannot flash.
    offset.set(-pending.direction * pending.entryDistance);
    contentOpacity.set(.72); progress.set(0); setPhase('enter');
    const currentAnimation = animationId.current;
    const spring = { type: 'spring', stiffness: 340, damping: 34, mass: .85, velocity: pending.velocity * 1000 };
    animations.current = [
      animate(offset, 0, { ...spring, onComplete: () => {
        if (animationId.current !== currentAnimation) return;
        setPhase('rest');
        if (pendingClick.current === clickGuard && clickGuard) clickGuard.expires = Date.now() + 500;
      } }),
      animate(contentOpacity, 1, { duration: .16, ease: 'easeOut' }),
    ];
  }, [transitionKey, clearHandoff, stopAnimation, offset, contentOpacity, progress, reset, setPhase, cancel]);

  useEffect(() => {
    // Capture-phase tracking also sees a second finger placed outside this surface.
    const down = event => {
      pendingClick.current = null; // A fresh touch or mouse press is a real tap.
      if (handoff.current) cancel(); // A new interaction supersedes queued navigation.
      if (event.pointerType === 'touch') touchPointers.current.add(event.pointerId);
      if (gesture.current && event.pointerId !== gesture.current.id) cancel();
    };
    const up = event => touchPointers.current.delete(event.pointerId);
    const interrupted = event => {
      up(event);
      if (gesture.current?.id === event.pointerId) cancel();
    };
    const inactive = () => { touchPointers.current.clear(); cancel(); };
    const visibility = () => { if (document.visibilityState === 'hidden') inactive(); };
    const keyboard = event => { if (!['Shift', 'Control', 'Alt', 'Meta'].includes(event.key) && (gesture.current || handoff.current)) cancel(); };
    const click = event => {
      const pending = pendingClick.current;
      if (!pending || event.detail === 0) { if (handoff.current) cancel(); return; } // Keyboard activation stays available.
      if (Date.now() > pending.expires) { pendingClick.current = null; if (handoff.current) cancel(); return; }
      const target = event.target;
      const related = target === pending.surface || pending.target?.contains(target) ||
        (!pending.target?.isConnected && pending.surface.contains(target));
      if (!related) { if (handoff.current) cancel(); return; }
      pendingClick.current = null;
      event.preventDefault(); event.stopImmediatePropagation();
    };
    document.addEventListener('click', click, true);
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('pointerup', up, true);
    document.addEventListener('pointercancel', interrupted, true);
    document.addEventListener('visibilitychange', visibility);
    document.addEventListener('keydown', keyboard, true);
    window.addEventListener('blur', inactive);
    return () => {
      document.removeEventListener('click', click, true);
      document.removeEventListener('pointerdown', down, true);
      document.removeEventListener('pointerup', up, true);
      document.removeEventListener('pointercancel', interrupted, true);
      document.removeEventListener('visibilitychange', visibility);
      document.removeEventListener('keydown', keyboard, true);
      window.removeEventListener('blur', inactive);
      releaseCapture(gesture.current);
      gesture.current = null;
      clearHandoff(); stopAnimation(); setPhase('rest');
    };
  }, [cancel, stopAnimation, clearHandoff, setPhase]);
  useEffect(() => { cancel(); }, [enabled, axis, reduced, cancel]);

  const blocked = () => !options.current.enabled || document.visibilityState === 'hidden' || (!options.current.allowDialog && document.querySelector('[aria-modal="true"]'));
  const bind = {
    // A nested surface owns its content even at its first/last item or when disabled.
    'data-swipe-surface': axis,
    onPointerDown: event => {
      const surface = event.currentTarget, target = event.target;
      if (blocked() || event.defaultPrevented || event.pointerType !== 'touch' || event.isPrimary === false || touchPointers.current.size > 1) return;
      if (!options.current.onPrevious && !options.current.onNext) return;
      if (target.closest?.('[data-swipe-surface]') !== surface || !canStart(target, surface, options.current.startAnywhere)) return;
      if (surface.contains(document.activeElement) && document.activeElement.matches?.(editable)) return;
      if (axis === 'x' && (event.clientX < 24 || event.clientX > window.innerWidth - 24)) return;
      if (scrollingAncestor(target, surface, axis)) return;
      event.stopPropagation();
      // Catch a settling page at its current position rather than snapping it
      // back under the finger before the next drag has even started.
      stopAnimation();
      if (contentOpacity.get() !== 1) {
        if (options.current.reduced) contentOpacity.set(1);
        else animations.current = [animate(contentOpacity, 1, { duration: .1, ease: 'easeOut' })];
      }
      const bounds = (visualRef.current || surface).getBoundingClientRect();
      feedbackY.set(Math.max(12, Math.min(Math.max(12, (bounds.height || window.innerHeight) - 40), event.clientY - bounds.top - 28)));
      gesture.current = { x: event.clientX, y: event.clientY, width: bounds.width || window.innerWidth, baseOffset: offset.get(), id: event.pointerId, locked: false, target: surface, tapTarget: target.closest?.('button,a,[role="button"],[role="link"]') || target, started: event.timeStamp, samples: [{ amount: 0, time: event.timeStamp }] };
    },
    onPointerMove: event => {
      const current = gesture.current;
      if (!current || event.pointerId !== current.id) return;
      event.stopPropagation();
      if (blocked()) { cancel(); return; }
      const dx = event.clientX - current.x, dy = event.clientY - current.y;
      if (!current.locked) {
        // Once scrolling wins, this touch can never become a navigation gesture.
        if (swipeIntent(dx, dy, axis === 'x' ? 'y' : 'x')) { cancel(); return; }
        if (!swipeIntent(dx, dy, axis)) return;
        current.locked = true;
        setPhase('drag');
        try { current.target.setPointerCapture?.(current.id); } catch (_) { cancel(); return; }
      }
      if (event.cancelable) event.preventDefault();
      const amount = axis === 'x' ? dx : dy;
      current.samples.push({ amount, time: event.timeStamp });
      while (current.samples.length > 2 && current.samples[1].time < event.timeStamp - 100) current.samples.shift();
      const action = amount > 0 ? options.current.onPrevious : options.current.onNext;
      if (!options.current.reduced) offset.set(swipeDragOffset(amount, { available: Boolean(action), width: current.width, baseOffset: current.baseOffset }));
      progress.set(action ? Math.max(-1, Math.min(1, amount / Math.max(1, options.current.threshold))) : 0);
    },
    onPointerUp: event => {
      const current = gesture.current;
      if (!current || event.pointerId !== current.id) return;
      event.stopPropagation();
      const dx = event.clientX - current.x, dy = event.clientY - current.y, amount = axis === 'x' ? dx : dy;
      const recent = current.samples.find(sample => sample.time >= event.timeStamp - 100) || current.samples[current.samples.length - 1];
      const elapsed = event.timeStamp - recent.time;
      const velocity = elapsed > 0 ? (amount - recent.amount) / elapsed : 0;
      const deliberateFlick = Math.abs(amount) >= FLICK_DISTANCE && event.timeStamp - current.started <= 300 && Math.abs(velocity) >= FLICK_SPEED && Math.sign(velocity) === Math.sign(amount);
      const commit = !blocked() && current.locked && swipeIntent(dx, dy, axis) && (Math.abs(amount) >= options.current.threshold || deliberateFlick);
      const action = amount > 0 ? options.current.onPrevious : options.current.onNext;
      if (current.locked && event.cancelable) event.preventDefault();
      gesture.current = null;
      suppressClick(current);
      releaseCapture(current);
      if (commit && action) {
        if (axis === 'x' && options.current.resetOnCommit && options.current.transitionKey !== undefined && visualRef.current && !options.current.reduced) {
          stopAnimation();
          const direction = Math.sign(amount), width = current.width;
          const continuingVelocity = Math.sign(velocity) === direction ? velocity : 0;
          const pending = { key: options.current.transitionKey, phase: 'exit', direction, velocity: Math.max(-.9, Math.min(.9, continuingVelocity)), entryDistance: Math.min(72, width * .18), timer: null, clickGuard: pendingClick.current };
          if (pending.clickGuard) pending.clickGuard.expires = Infinity;
          handoff.current = pending; setPhase('exit');
          const exitDistance = direction * Math.min(width * .7, Math.abs(offset.get()) + Math.max(36, width * .1));
          animations.current = [
            animate(offset, exitDistance, { duration: .095, ease: [ .2, .65, .35, 1 ], onComplete: () => {
              if (handoff.current !== pending) return;
              if (blocked()) { reset(); return; }
              pending.phase = 'awaiting'; setPhase('handoff');
              let accepted;
              try { accepted = action(); } catch (_) { reset(); return; }
              if (handoff.current !== pending) return;
              if (accepted === false) { reset(); return; }
              // A rejected, guarded or no-op callback cannot leave its current
              // page invisible while waiting for a content identity change.
              pending.timer = setTimeout(() => { if (handoff.current === pending) reset(); }, 120);
            } }),
            animate(contentOpacity, 0, { duration: .095, ease: 'easeOut' }),
            animate(progress, 0, { duration: .095 }),
          ];
        } else {
          action();
          if (options.current.resetOnCommit) reset();
        }
      } else reset();
    },
    onPointerCancel: event => { if (gesture.current?.id === event.pointerId) cancel(); },
    onLostPointerCapture: event => {
      // Touch initially captures the hit child. Taking capture on this surface
      // emits a bubbling loss from that child; only losing our own capture cancels.
      const current = gesture.current;
      if (current?.id === event.pointerId && event.target === current.target) cancel();
    },
  };
  return { bind, offset, contentOpacity, progress, feedbackY, visualRef };
}

const hintStyle = {
  position: 'absolute', top: 12, zIndex: 3, display: 'inline-flex', alignItems: 'center', gap: 6,
  maxWidth: 'min(180px, 45%)', padding: '6px 10px', borderRadius: 999,
  border: '1px solid var(--c-s3, #27302f)', background: 'var(--c-s1, #121717)', color: 'var(--c-t2, #c2cec8)',
  fontSize: 11, fontWeight: 600, lineHeight: 1.4, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', pointerEvents: 'none',
};

const SwipeContext = createContext(null);

// The gesture scope stays still, keeping sticky headers and fixed navigation in
// their own coordinate space. Only the selected content stage follows the drag.
export function SwipeScope({ children, onPrevious, onNext, previousLabel, nextLabel, enabled = true, startAnywhere = false, allowDialog = false, transitionKey, className = '', style, ...props }) {
  const swipe = useSwipeMotion({ onPrevious, onNext, enabled, startAnywhere, allowDialog, transitionKey });
  const active = enabled && Boolean(onPrevious || onNext);
  return <SwipeContext.Provider value={{ swipe, onPrevious, onNext, previousLabel, nextLabel, enabled }}>
    <div {...props} {...swipe.bind} className={`tl-swipe-scope ${className}`} style={{ ...style, overflowX: 'clip', touchAction: active ? 'pan-y pinch-zoom' : style?.touchAction }}>{children}</div>
  </SwipeContext.Provider>;
}

function SwipePresentation({ swipe, bind, children, onPrevious, onNext, previousLabel, nextLabel, enabled = true, className = '', style, ...props }) {
  const { offset, contentOpacity, progress, feedbackY, visualRef } = swipe;
  const previousOpacity = useTransform(progress, [0, .18, .75, 1], [0, 0, .9, 1]);
  const nextOpacity = useTransform(progress, [-1, -.75, -.18, 0], [1, .9, 0, 0]);
  const active = enabled && Boolean(onPrevious || onNext);
  return <motion.div {...props} {...bind} ref={visualRef} className={`tl-swipe-surface ${className}`} style={{ position: 'relative', ...style, x: offset, opacity: contentOpacity, touchAction: active ? 'pan-y pinch-zoom' : style?.touchAction }}>
    {enabled && onPrevious && previousLabel && <motion.span aria-hidden="true" className="tl-swipe-destination tl-swipe-destination-previous" style={{ ...hintStyle, top: feedbackY, left: 12, opacity: previousOpacity }}>← {previousLabel}</motion.span>}
    {enabled && onNext && nextLabel && <motion.span aria-hidden="true" className="tl-swipe-destination tl-swipe-destination-next" style={{ ...hintStyle, top: feedbackY, right: 12, opacity: nextOpacity }}>{nextLabel} →</motion.span>}
    {children}
  </motion.div>;
}

function StandaloneSwipeSurface({ onPrevious, onNext, enabled = true, allowDialog = false, startAnywhere = false, transitionKey, ...props }) {
  const swipe = useSwipeMotion({ onPrevious, onNext, enabled, allowDialog, startAnywhere, transitionKey });
  return <SwipePresentation {...props} swipe={swipe} bind={swipe.bind} onPrevious={onPrevious} onNext={onNext} enabled={enabled} />;
}

export default function SwipeSurface({ scoped = false, ...props }) {
  const scope = useContext(SwipeContext);
  if (scoped && scope) return <SwipePresentation {...scope} {...props} swipe={scope.swipe} />;
  return <StandaloneSwipeSurface {...props} />;
}
